import React, { useRef } from 'react';
import { Mention, MentionsInput } from 'react-mentions';

import { getMentionableUsers } from '../../../utils/backend-api/mentionableUsers';

type MentionableInputProps = {
  name: string;
  required: boolean;
  className?: string;
  autoFocus?: boolean;
  value: string;
  onChange: (e: React.ChangeEvent<unknown>) => void;
  contentId: number;
  contentType: string;
};

export default function MentionableInput({
  name,
  required,
  className,
  autoFocus,
  value,
  onChange,
  contentId,
  contentType,
}: MentionableInputProps) {
  const mentionInputRef = useRef<HTMLInputElement | null>();
  const fetchUsers = (query, callback) => {
    getMentionableUsers({ query: query, commentableId: contentId, commentableType: contentType })
      .then((res) =>
        res.users.map((user) => ({
          name: user.name,
          display: `@${user.username}`,
          id: user.id,
          username: user.username,
          pictureUrl: user.picture_url,
          companyName: user.company_name,
          title: user.title_at_company,
        }))
      )
      .then(callback)
      .catch((e) => {
        console.error('Error loading users: ', e);
      });
  };

  const renderMentionSuggestion = (entry, search) => {
    // Check console to see the number of times it executes for each character
    const nameRegex = new RegExp(search.substring(1).split('').join('\\W*'), 'i'); // abc -> /a\W*b\W*c/i
    // Wrap <span> around the matching part of the name
    const nameWithHighlight = entry.name.replace(nameRegex, '$&');
    const entryTitle = [entry.title, entry.companyName].filter((c) => !!c).join(' - ');
    if (entry.pictureUrl) {
      return (
        <>
          <div className="tributeImgContainer">
            <img src={entry.pictureUrl} alt={entry.name} />
          </div>
          <div className="tributeTextContainer">
            <div>
              <span className="tributeAutocomleteHighlighted">{nameWithHighlight}</span>
            </div>
            <div className="tributeTitle">{entryTitle}</div>
          </div>
        </>
      );
    } else {
      return (
        <>
          <div className="tributeImgContainer">
            <div className="placeholder">{entry.name[0].toUpperCase()}</div>
          </div>
          <div className="tributeTextContainer">
            <div>{nameWithHighlight}</div>
            <div className="tributeTitle">{entryTitle}</div>
          </div>
        </>
      );
    }
  };

  const handleBodyChange = (e, handleChange) => {
    const fabricatedEvent = { ...e, target: { ...e.target, name: 'body' } };
    handleChange(fabricatedEvent);
  };

  return (
    <MentionsInput
      name={name}
      required={required}
      className={className}
      autoFocus={autoFocus}
      onChange={(e) => handleBodyChange(e, onChange)}
      inputRef={mentionInputRef}
      value={value}
    >
      <Mention
        trigger="@"
        data={fetchUsers}
        renderSuggestion={renderMentionSuggestion}
        markup="[__display__]"
        appendSpaceOnAdd={true}
      />
    </MentionsInput>
  );
}
